import { Injectable } from '@angular/core';
import { FirebaseService } from './firebase.service';
import { User, UserJourney } from '../models/simskillsModels';
import { Journey } from '../dialogs/user-detail-dialog/user-detail-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class UserInfoService {
  user: any;
  
  constructor(private firebaseService: FirebaseService) {
  }
  
  public isUserAuthorized(uid: string) {
    return new Promise<any>((resolve, reject) => {
      this.firebaseService.getDocument('/authorized_users/' + uid).then(
        (res: any) => {
          resolve(res);
        },
        (err: any) => {
          reject(err);
        }
      );
    });
  }
  
  public async changeAuthorized(uid: string) {
    const authorized = await this.isUserAuthorized(uid);
    // Autorize the user
    this.firebaseService.updateDocument('/authorized_users', uid, {
      approved: !authorized.approved,
    });
  }
  
  public async addUser(companyName: string, user: any): Promise<any> {
    return this.firebaseService.addDocument(`/added_users/${companyName}/`, user).then(res => {
      return res;
    });
  }
  
  public async getCompanyJourney(company: string, department?: string, showInactiveOnly: boolean = false): Promise<any> {
    const result: any = {
      registerUsers: 0,
      notRegisterUsers: 0,
      totalUsers: 0,
    };
    const companyLowerCase = company.replaceAll('.', '').toLowerCase();
    if (!company) {
      return {};
    }
    
    let journeys, users;
    if (department) [journeys, users] = await Promise.all([this.firebaseService.getDocumentsListOnce('/user_journey/' + companyLowerCase, {
      orderByChild: 'department',
      equalTo: department
    }), this.getCompanyAddedUsers(company, department)]);
    else [journeys, users] = await Promise.all([this.firebaseService.getDocumentsListOnce('/user_journey/' + companyLowerCase), this.getCompanyAddedUsers(company)]);
    journeys = journeys.filter(j => showInactiveOnly ? !!j.inactiveAt : !j.inactiveAt);
    if (showInactiveOnly) {
      users = [];
    }
    result.registerUsers = journeys.length;
    result.notRegisterUsers = users.length;
    result.totalUsers = result.registerUsers + result.notRegisterUsers;
    
    
    let journey = {};
    for (const journeyElement of journeys) {
      journey[journeyElement.id] = {
        ...journeyElement,
        source: 'user_journey',
      };
    }
    if (!journey) journey = {};
    for (let key in users) {
      users[key].active = false;
      users[key].source = 'added_users';
      if (typeof users[key].module === 'string') {
        users[key].module = [users[key].module];
      }
      journey[key] = users[key];
    }
    for (let key in journey) journey[key].company = company;
    
    result.journey = journey;
    return result;
  }
  
  public getCompanyAddedUsers(company: string, department?: string) {
    const companyLowerCase = company.replaceAll('.', '').toLowerCase();
    return new Promise<Journey>((resolve, reject) => {
      this.firebaseService.getDocumentsListOnce('/added_users/' + companyLowerCase, {
        orderByChild: 'department',
        equalTo: department
      }).then((res: any) => {
        resolve(res);
      }, (err: any) => {
        reject(err);
      });
    });
  }
  
  public getJourney() {
    return new Promise<Journey>((resolve, reject) => {
      this.firebaseService.getDocument('/user_journey').then((res: Journey) => {
          let journey: Journey = res as Journey;
          resolve(journey);
        },
        (err: any) => {
          reject(err);
        });
    });
  }
  
  public getUserAssessments(uid: string) {
    return new Promise<any>((resolve, reject) => {
      this.firebaseService.getDocument('/user_assessments/' + uid).then(
        (res: any) => {
          //  let userAssessments: user = res as UserSimulations;
          resolve(res);
        },
        (err: any) => {
          reject(err);
        }
      );
    });
  }
  
  public getUserScheduledSimulations(uid: string) {
    return new Promise<any>((resolve, reject) => {
      this.firebaseService
        .getDocument('/user_scheduled_simulations/' + uid)
        .then(
          (res: any) => {
            let userScheduledSimulations: any =
              res as any;
            resolve(userScheduledSimulations);
          },
          (err: any) => {
            reject(err);
          }
        );
    });
  }
  
  public getUsers() {
    return new Promise<any>((resolve, reject) => {
      this.firebaseService.getDocument('/users/').then(
        (res: any) => {
          //let userData: User = res as User;
          resolve(res);
        },
        (err: any) => {
          reject(err);
        }
      );
    });
  }
  
  public getUser(uid: string) {
    return new Promise<User>((resolve, reject) => {
      this.firebaseService.getDocument('/users/' + uid).then(
        (res: User) => {
          let userData: User = res as User;
          resolve(userData);
        },
        (err: any) => {
          reject(err);
        }
      );
    });
  }
  
  public async getUserCompanyId(uid: string): Promise<string> {
    return await this.firebaseService.getDocument(`/users/${uid}/companyId`);
  }
  
  public getCompanies() {
    return new Promise<any>((resolve, reject) => {
      this.firebaseService.getDocumentsListOnce('/companies', {orderByChild: 'name_lowercase'}).then((res: any) => {
          let companies: any = res as any;
          companies = companies.filter(c => c.active).sort((a, b) => {
            if (a.name_lowercase < b.name_lowercase) return -1;
            if (a.name_lowercase > b.name_lowercase) return 1;
            return 0;
          });
          resolve(companies);
        },
        (err: any) => {
          reject(err);
        }
      );
    });
  }
  
  public validateExistUser = async (email: string, company: string): Promise<any> => {
    const [users, addedUsers] = await Promise.all([
      this.firebaseService.getDocumentsListOnce('/users', {
        orderByChild: 'email',
        equalTo: email,
      }),
      this.firebaseService.getDocumentsListOnce(`/added_users/${company}`, {
        orderByChild: 'email',
        equalTo: email,
      }),
    ]);

    return {
      exists: !!(users?.length || addedUsers?.length),
      user: users?.[0] || null,
      addedUser: addedUsers?.[0] || null,
    };
  };
  
  public getCompany(uid: string) {
    return new Promise<any>((resolve, reject) => {
      this.firebaseService.getDocument('/companies' + uid).then(
        (res: any) => {
          let company: any = res as any;
          resolve(company);
        },
        (err: any) => {
          reject(err);
        }
      );
    });
  }
}
